import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import Modal from '../Modal';
import { InlineError } from './shared';
import { useConfirmLock } from '../../hooks/useConfirmLock';

/**
 * Confirmation dialog that optionally collects a reason — used for the
 * business actions that are audited with a reason (archive/restore customer,
 * reject/cancel quote, terminate contract, publish document, ...).
 */
export default function ReasonModal({
  title,
  description,
  confirmLabel,
  reasonLabel,
  reasonRequired = false,
  minLength = 0,
  danger = false,
  loading = false,
  error,
  children,
  onConfirm,
  onCancel,
}: {
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  /** Omit to show a plain confirmation without a reason field. */
  reasonLabel?: string;
  reasonRequired?: boolean;
  minLength?: number;
  danger?: boolean;
  loading?: boolean;
  error?: string | null;
  children?: ReactNode;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  const needed = reasonRequired ? Math.max(1, minLength) : 0;
  const tooShort = !!reasonLabel && trimmed.length < needed;
  const confirmOnce = useConfirmLock(loading); // DQ-126/161: lock at the click

  return (
    <Modal open title={title} onClose={onCancel} sizeClass="max-w-md"
      icon={danger ? <AlertTriangle size={18} className="text-danger-600" /> : undefined}>
      <div className="space-y-4">
        {description && <div className="text-sm text-surface-600">{description}</div>}
        {children}
        {reasonLabel && (
          <div>
            <label className="label" htmlFor="reason-modal-input">
              {reasonLabel}{reasonRequired ? ' *' : ''}
            </label>
            <textarea
              id="reason-modal-input"
              rows={3}
              className="input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            {reasonRequired && minLength > 1 && (
              <p className="text-xs text-surface-500 mt-1">{t('sales.reasonMinLength', { count: minLength })}</p>
            )}
          </div>
        )}
        <InlineError>{error}</InlineError>
        <div className="flex gap-2 justify-end pt-1">
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
            disabled={loading || tooShort}
            onClick={() => confirmOnce(() => onConfirm(trimmed))}
          >
            {loading ? t('common.saving') : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
